import { useEffect, useMemo, useState } from 'react';
import { ListBox, ListBoxItem, ToggleButton, ToggleButtonGroup } from 'react-aria-components';

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const REVIEW_STATUSES = new Set(['PENDING', 'ACCEPTED', 'REJECTED']);
const REVIEW_STATUS_ORDER = {ACCEPTED: 0, PENDING: 1, REJECTED: 2};

const getActivityTerms = (term) => (
  typeof term === 'string'
    ? term.normalize('NFKC').match(/[\p{L}\p{N}]+/gu) || []
    : []
);

const getActivityName = (activity) => (
  typeof activity === 'string'
    ? activity
    : activity?.activity_name || activity?.name || activity?.activity
);

const getActivityIdentity = (activity) => (
  String(activity.activity_id || activity.id || activity.activity_name || '').trim().toLowerCase()
);

const getReviewStatus = (activity) => {
  if (REVIEW_STATUSES.has(activity?.review_status)) return activity.review_status;
  return activity?.is_extracted === true ? 'ACCEPTED' : 'PENDING';
};

const getActivityKey = (activity, index) => (
  String(activity.activity_id || `${activity.activity_name}-${index}`)
);

function HIGHLIGHTED_TEXT({ text, term }) {
  if (typeof text !== 'string' || !term) return text;

  const termParts = getActivityTerms(term);
  if (!termParts.length) return text;

  const highlightExpression = termParts
    .sort((first, second) => second.length - first.length)
    .map(escapeRegExp)
    .join('|');
  const parts = text.split(new RegExp(
    `(?<![\\p{L}\\p{N}])(${highlightExpression})(?![\\p{L}\\p{N}])`,
    'giu',
  ));
  return parts.map((part, index) => (
    new RegExp(
      `^(?:${highlightExpression})$`,
      'iu',
    ).test(part)
      ? <span className='tab-activities__highlight' key={index}>{part}</span>
      : part
  ));
}

export default function TAB_ACTIVITIES({ analysis, activityCatalog, loading, error, providerData, updateActivities }) {
  const activities = useMemo(() => {
    const matches = analysis?.activity_matches || [];
    const existing = providerData?.extractions?.activities || [];
    const existingByIdentity = new Map();
    existing.forEach((activity) => {
      const identity = getActivityIdentity({
        activity_id: activity?.id || activity?.activity_id,
        activity_name: getActivityName(activity)
      });
      if (identity) existingByIdentity.set(identity, activity);
    });

    const mergedMatches = matches.map((activity) => {
      const existingActivity = existingByIdentity.get(getActivityIdentity(activity));
      const source = existingActivity?.source || [];

      return {
        ...existingActivity,
        ...activity,
        review_status: getReviewStatus({
          ...activity,
          ...existingActivity,
          is_extracted: existingActivity?.is_extracted === true || activity.is_extracted === true
        }),
        source: [...new Set([...source, 'text_match'])]
      };
    });

    const matchIdentities = new Set(mergedMatches.map(getActivityIdentity));
    const savedOnly = existing
      .filter((activity) => {
        const identity = getActivityIdentity({
          activity_id: activity?.id || activity?.activity_id,
          activity_name: getActivityName(activity)
        });
        return identity && !matchIdentities.has(identity);
      })
      .map((activity) => ({
        ...activity,
        activity_id: activity.id || activity.activity_id || null,
        activity_name: getActivityName(activity),
        review_status: getReviewStatus(activity),
        evidence: activity.evidence || [],
        source: activity.source || ['provider_json']
      }));

    return [...mergedMatches, ...savedOnly].sort((first, second) => (
      REVIEW_STATUS_ORDER[getReviewStatus(first)] - REVIEW_STATUS_ORDER[getReviewStatus(second)]
    ));
  }, [analysis, providerData]);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [activityToAdd, setActivityToAdd] = useState('');
  const [isAddingActivity, setIsAddingActivity] = useState(false);
  const selectedActivity = activities.find((activity, index) => (
    getActivityKey(activity, index) === selectedActivityId
  ));
  const availableActivities = useMemo(() => {
    const existingIds = new Set(activities.map((activity) => (
      String(activity.activity_id || activity.id || '')
    )));
    return (activityCatalog.length ? activityCatalog : analysis?.activities || []).filter((activity) => (
      activity.id &&
      (activity.activity || activity.name) &&
      !existingIds.has(String(activity.id))
    )).map((activity) => ({
      id: activity.id,
      name: activity.name || activity.activity
    }));
  }, [activities, activityCatalog, analysis]);

  useEffect(() => {
    setSelectedActivityId((currentSelectedId) => {
      if (!activities.length) return null;
      if (currentSelectedId && activities.some((activity, index) => (
        getActivityKey(activity, index) === currentSelectedId
      ))) {
        return currentSelectedId;
      }
      return getActivityKey(activities[0], 0);
    });
  }, [activities]);

  const evidence = selectedActivity?.evidence || [];
  const matchCount = selectedActivity?.match_count ?? evidence.length;
  const pageCount = selectedActivity?.page_count ??
    new Set(evidence.map((item) => item.page_url).filter(Boolean)).size;
  const groupedEvidence = useMemo(() => {
    const groups = new Map();

    evidence.forEach((item) => {
      const pageUrl = item.page_url || 'Unknown page';
      if (!groups.has(pageUrl)) groups.set(pageUrl, []);
      groups.get(pageUrl).push(item);
    });

    return [...groups.entries()].map(([pageUrl, items]) => ({pageUrl, items}));
  }, [evidence]);

  const updateReviewStatus = (reviewStatus) => {
    const currentIndex = activities.findIndex((activity, index) => (
      getActivityKey(activity, index) === selectedActivityId
    ));
    const nextIndex = currentIndex >= 0 && activities.length > 1
      ? (currentIndex + 1) % activities.length
      : currentIndex;
    const updatedActivities = activities.map((activity) => (
      getActivityKey(activity, activities.indexOf(activity)) === selectedActivityId
        ? {...activity, review_status: reviewStatus}
        : activity
    ));
    updateActivities?.(updatedActivities);
    if (nextIndex >= 0) {
      setSelectedActivityId(getActivityKey(activities[nextIndex], nextIndex));
    }
  };

  const restoreActivityView = () => {
    setActivityToAdd('');
    setIsAddingActivity(false);
    setSelectedActivityId((currentSelectedId) => {
      if (!activities.length) return null;
      const currentIndex = activities.findIndex((activity, index) => (
        getActivityKey(activity, index) === currentSelectedId
      ));
      const nextIndex = currentIndex >= 0
        ? (currentIndex + 1) % activities.length
        : 0;
      return getActivityKey(activities[nextIndex], nextIndex);
    });
  };

  const addActivity = () => {
    const activity = availableActivities.find((candidate) => (
      String(candidate.id) === activityToAdd
    ));
    if (!activity) return;

    updateActivities?.([
      ...activities,
      {
        activity_id: activity.id,
        activity_name: activity.name,
        review_status: 'ACCEPTED',
        is_extracted: false,
        evidence: [],
        source: ['manual']
      }
    ]);
    restoreActivityView();
  };

  if (loading) return <p>Loading activity analysis...</p>;
  if (error) return <p>Unable to load activity analysis.</p>;

  return (
    <div className='tab-activities'>
      <nav className='tab-activities__nav'>
        <ListBox
          aria-label='Activity matches'
          selectionMode='single'
          selectedKeys={selectedActivityId ? [selectedActivityId] : []}
          onSelectionChange={(keys) => {
            const selectedKey = Array.from(keys)[0];
            if (selectedKey) setSelectedActivityId(selectedKey);
          }}
        >
          {activities.map((activity, index) => (
            <ListBoxItem
              id={getActivityKey(activity, index)}
              key={getActivityKey(activity, index)}
              data-review-status={getReviewStatus(activity)}
            >
              <div className='tab-activities__status-icon' aria-hidden='true' />
            <span className='tab-activities__item-label'>{activity.activity_name}</span>
            </ListBoxItem>
          ))}
        </ListBox>
        <button
          type='button'
          className='btn btn--secondary tab-activities__add-button'
          onClick={() => setIsAddingActivity(true)}
        >
          Add an activity
        </button>
      </nav>

      <div className='tab-activities__content'>
        {isAddingActivity ? (
          <div className='tab-activities__add'>
            <label htmlFor='activity-to-add'>Add activity</label>
            <select
              id='activity-to-add'
              value={activityToAdd}
              onChange={(event) => setActivityToAdd(event.target.value)}
            >
              <option value=''>Select an activity</option>
              {availableActivities.map((activity) => (
                <option value={activity.id} key={activity.id}>{activity.name}</option>
              ))}
            </select>
            <button
              type='button'
              className='btn btn--primary'
              onClick={addActivity}
              disabled={!activityToAdd}
            >
              Accept
            </button>
            <button
              type='button'
              className='btn btn--secondary'
              onClick={restoreActivityView}
            >
              Cancel
            </button>
          </div>
        ) : (
          <>
            {!activities.length && <p className='tab-activities__empty'>No activity matches</p>}
            {selectedActivity && (
          <section>
            <div className='tab-activities__header text--md medium'>
              <div className='tab-activities__header-content'>
                <span>{selectedActivity.activity_name}</span>
                <div className='tab-activities__subheader text--sm color--gray--500'>
                  Found {matchCount} {matchCount === 1 ? 'time' : 'times'} on {pageCount} {pageCount === 1 ? 'page' : 'pages'}
                </div>
              </div>
              <ToggleButtonGroup
                aria-label={`Review status for ${selectedActivity.activity_name}`}
                selectedKeys={new Set([selectedActivity.review_status])}
                selectionMode='single'
                onSelectionChange={(keys) => {
                  const selectedStatus = Array.from(keys)[0];
                  if (REVIEW_STATUSES.has(selectedStatus)) {
                    updateReviewStatus(selectedStatus);
                  }
                }}
              >
                <ToggleButton id='REJECTED'>Rejected</ToggleButton>
                <ToggleButton id='PENDING'>Pending</ToggleButton>
                <ToggleButton id='ACCEPTED'>Accepted</ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div className='tab-activities__evidence-list'>
              {groupedEvidence.length ? groupedEvidence.map(({pageUrl, items}) => (
                <article className='tab-activities__evidence' key={pageUrl}>
                  {pageUrl !== 'Unknown page' && (
                    <a
                      className='tab-activities__page-link text--sm italic color--gray--500'
                      href={pageUrl}
                      target='_blank'
                      rel='noreferrer'
                    >
                      {pageUrl}
                    </a>
                  )}
                  {items.map((item, index) => (
                    <div className='tab-activities__evidence-text' key={`${pageUrl}-${index}`}>
                      ...<HIGHLIGHTED_TEXT text={item.text} term={selectedActivity.activity_name} />...
                    </div>
                  ))}
                </article>
              )) : <p>No evidence found for this activity.</p>}
            </div>
          </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}